import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { LocalStorageService } from 'ngx-webstorage';

export interface OrganizationContextSynchronization {
  authoritativeOrganizationId: string | null;
  previousOrganizationId: string | null;
  changed: boolean;
}

@Injectable({ providedIn: 'root' })
export class OrganizationContextService {
  constructor(private jwtHelper: JwtHelperService, private localStorageService: LocalStorageService) {}

  synchronizeFromToken(token: string): OrganizationContextSynchronization {
    const authoritativeOrganizationId = this.getOrganizationIdFromToken(token);
    const previousOrganizationId = this.normalizeOrganizationId(this.localStorageService.retrieve('organizationId'));

    if (!authoritativeOrganizationId) {
      return {
        authoritativeOrganizationId: null,
        previousOrganizationId,
        changed: false,
      };
    }

    const changed = previousOrganizationId !== authoritativeOrganizationId;
    if (changed) {
      this.localStorageService.store('organizationId', authoritativeOrganizationId);
    }

    return {
      authoritativeOrganizationId,
      previousOrganizationId,
      changed,
    };
  }

  repairRequestUrl(url: string, synchronization: OrganizationContextSynchronization): string {
    if (!synchronization.authoritativeOrganizationId) {
      return url;
    }

    const authenticatedOrganizationSegment = new RegExp(
      '(/organizations/)([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})' +
      '(?=/(?:purchase_orders(?:/|\\?|$)|messages/broadcast(?:/|\\?|$)))',
      'i',
    );

    return url.replace(authenticatedOrganizationSegment, `$1${synchronization.authoritativeOrganizationId}`);
  }

  private getOrganizationIdFromToken(token: string): string | null {
    if (!token) {
      return null;
    }

    try {
      const claims = this.jwtHelper.decodeToken(token);
      return this.normalizeOrganizationId(claims && claims.organization_id);
    } catch (error) {
      return null;
    }
  }

  private normalizeOrganizationId(value: any): string | null {
    if (typeof value !== 'string' || !value) {
      return null;
    }

    return value.replace(/^"(.*)"$/, '$1');
  }
}
